import _                from 'underscore';
import moment           from 'moment';
import * as Yup         from 'yup';
import { StringSchema } from 'yup';

import { PASSWORD_REQUIREMENTS_REGEX, SYSTEM_STRING_SIMILARITY_DEFAULT_THRESHOLD } from '@src/constants';
import { findTopSimilarityString, mergeDeepRetainKeys }                            from "@src/util";
import { TimeService }                                                             from '../services';
import { T_OR_NULL }                                                               from '../types';


let numeral = require( 'numeral' );


interface customTestFunction {
    ( schemaName?: string, ...other: any ): void
}

interface customTestFunctionWithOptions<T> {
    ( schemaName?: string, options?: T ): void
}

interface customTransformFunction<V> {
    ( currentValue: string | number | null | undefined, originalValue?: unknown ): V
}

type MinMaxCharTestOptions = {

    // Error message to display when the field does not meet the min char requirement
    errorMessage?: string,

    // Whether the field is required when the min char requirement is enforced
    requiredWhenEnforced?: boolean,

    // Message when the field is required and isn't valid
    requiredWhenEnforcedMessage?: string,

    // Whether the field is nullable when the min char requirement is not enforced
    nullableWhenNotEnforced?: boolean,
};

type EnforceableMinMaxCharTestOptions = {

    // The type of test to use with the character count
    charBoundary: 'min' | 'max',

    // The min/max number of characters allowed for the string
    charLimit: number,
};

type DescriptorMinCharTestOptions = MinMaxCharTestOptions & {

    // minimum number of characters required for a descriptor
    minChar?: number,
};

type DescriptorMaxCharTestOptions = MinMaxCharTestOptions & {

    // minimum number of characters required for a descriptor
    maxChar?: number,
};

type StringDifferentiationTestOptions = {

    // The string set to differentiate on
    stringsToCompare?: String[];

    // max score the similarity can have without being too similar
    maxScoreThreshold?: number,

    // Error message to display when the field does not meet the min char requirement
    errorMessage?: string,
}

/* Alter the interface for the schemas being added to */
declare module "yup" {

    interface StringSchema {

        isDateFormat(): StringSchema;

        isDateRangeFormat(): StringSchema;

        isNumeric(): StringSchema;

        isUSPhoneNumber(): StringSchema;

        isValidPassword(): StringSchema;

        hasDescriptorMinChar( options?: DescriptorMinCharTestOptions ): StringSchema;

        hasStringDifferentiation( options?: StringDifferentiationTestOptions ): StringSchema;

        transformCurrencyValue(): customTransformFunction<T_OR_NULL<number>>,

        transformEmptyStringToNull(): customTransformFunction<T_OR_NULL<number>>,

        transformNullToEmptyString(): customTransformFunction<T_OR_NULL<number>>,
    }
}

export const addStringIsDateFormatTest: customTestFunction = ( schemaName = 'isDateFormat', format: string = TimeService.getDisplayDateFormat() ) => {

    Yup.addMethod( Yup.string, schemaName, function( formats, parseStrict ){

        return ( this as StringSchema ).test( {
            name:    'isDateFormatTest',
            message: `Date field should be formatted as ${ format }`,
            test:    ( value: string ) => {
                return (
                    moment( value, format ).isValid() &&
                    moment( value ).format( format ) !== 'Invalid date'
                );
            }
        } );
    } );
}

export const addStringIsDateRangeFormatTest: customTestFunction = ( schemaName = 'isDateRangeFormat', format: string = TimeService.getDisplayDateFormat() ) => {

    Yup.addMethod( Yup.string, schemaName, function( formats, parseStrict ){

        return ( this as StringSchema ).test( {
            name:    'isDateRangeFormatTest',
            message: `Date range field should be formatted as ${ format } - ${ format }`,
            test:    ( value: string ) => {

                if( !_.isString( value ) ) return false;

                let [ startDate, endDate ] = value.split( ' - ' );

                let startDateValid = moment( startDate, format ).isValid(),
                    endDateValid   = moment( endDate, format ).isValid();

                return ( startDateValid && endDateValid );
            }
        } );
    } );
}

export const addStringIsNumericTest: customTestFunction = ( schemaName = 'isNumeric' ) => {

    Yup.addMethod( Yup.string, schemaName, function(){

        const digitRegExp = /^\d+$/

        return ( this as StringSchema ).matches( digitRegExp, 'Field value should contain only digits (0-9)' )
    } )
}


export const addStringDifferentiationTest: customTestFunctionWithOptions<StringDifferentiationTestOptions> = ( schemaName = 'hasStringDifferentiation', options = {
    stringsToCompare:  [],
    maxScoreThreshold: SYSTEM_STRING_SIMILARITY_DEFAULT_THRESHOLD,
    errorMessage:      '',
} ) => {

    Yup.addMethod( Yup.string, schemaName, function( _options: ( typeof options ) = options ){

        const mergedOptions = mergeDeepRetainKeys( options, _options );

        mergedOptions.errorMessage = options.errorMessage ?? `String characters are too similar.`;
        mergedOptions.maxScoreThreshold = options.maxScoreThreshold ?? SYSTEM_STRING_SIMILARITY_DEFAULT_THRESHOLD;

        return Yup.string().test( {

            name:    'check-string-similarity',
            message: mergedOptions.errorMessage,
            test:    ( value ) => {

                const {
                          maxScoreThreshold = options.maxScoreThreshold,
                          stringsToCompare  = options.stringsToCompare,
                          // errorMessage      = options.errorMessage ?? `String characters are too similar.`
                      } = mergedOptions;

                // const similarityResults = findSimilarStrings( value, stringsToCompare, maxScoreThreshold ),
                //       resultScores      = _.pluck( similarityResults, 'score' ),
                //       maxScore          = Math.max( ...resultScores ),
                //       maxResultScore    = [ -Infinity, NaN ].includes( maxScore ) ? 0 : maxScore,
                //       maxResultMatch    = _.findWhere( similarityResults, { score: maxResultScore } ) ?? { match: '', score: -1, algorithm: null };
                //
                // console.table( similarityResults );
                // console.log( similarityResults, maxResultMatch );

                const maxResultMatch = findTopSimilarityString( value, stringsToCompare, maxScoreThreshold )

                // @TODO: Yup 1.0
                // If we didn't differentiate the string enough, throw Yup error
                // if( maxResultScore > maxScoreThreshold ){
                //     return ctx.createError( { message: errorMessage.replace( STRING_CONVERSION.PLACEHOLDER, maxResultMatch?.match ?? '' ) } );
                // }
                // return true;

                console.log( maxResultMatch );

                return maxResultMatch ? ( maxResultMatch.score < maxScoreThreshold ) : true
            }
        } );
    } )
}


export const addStringMinCharDescriptorTest: customTestFunctionWithOptions<DescriptorMinCharTestOptions> = ( schemaName = 'hasDescriptorMinChar', options = {
    minChar:                     20,
    errorMessage:                '',
    requiredWhenEnforced:        true,
    requiredWhenEnforcedMessage: 'Please enter a description.',
    nullableWhenNotEnforced:     true,
} ) => {

    Yup.addMethod( Yup.string, schemaName, function( _options: ( typeof options ) = options ){

        const mergedOptions = mergeDeepRetainKeys( options, _options );

        mergedOptions.errorMessage = _options.errorMessage ?? `Please enter a clear and explanative description using a minimum of ${ mergedOptions.minChar } characters.`;

        return enforceableMinMaxCharTest( ( this as StringSchema ), {
            charBoundary:                'min',
            charLimit:                   mergedOptions.minChar,
            errorMessage:                mergedOptions.errorMessage,
            requiredWhenEnforced:        mergedOptions.requiredWhenEnforced,
            requiredWhenEnforcedMessage: mergedOptions.requiredWhenEnforcedMessage,
            nullableWhenNotEnforced:     mergedOptions.nullableWhenNotEnforced,
        } );

    } )
}

export const addStringMaxCharDescriptorTest: customTestFunctionWithOptions<DescriptorMaxCharTestOptions> = ( schemaName = 'hasDescriptorMaxChar', options = {
    maxChar:                     256,
    errorMessage:                '',
    requiredWhenEnforced:        true,
    requiredWhenEnforcedMessage: 'Please enter a description.',
    nullableWhenNotEnforced:     true,
} ) => {

    Yup.addMethod( Yup.string, schemaName, function( _options: ( typeof options ) = options ){

        const mergedOptions = mergeDeepRetainKeys( options, _options );

        mergedOptions.errorMessage = _options.errorMessage ?? `Please reduce this description to a maximum of ${ mergedOptions.minChar } characters.`;

        return enforceableMinMaxCharTest( ( this as StringSchema ), {
            charBoundary:                'max',
            charLimit:                   mergedOptions.minChar,
            errorMessage:                mergedOptions.errorMessage,
            requiredWhenEnforced:        mergedOptions.requiredWhenEnforced,
            requiredWhenEnforcedMessage: mergedOptions.requiredWhenEnforcedMessage,
            nullableWhenNotEnforced:     mergedOptions.nullableWhenNotEnforced,
        } );

    } )
}

export const enforceableMinMaxCharTest = ( subject: StringSchema, options: Required<MinMaxCharTestOptions & EnforceableMinMaxCharTestOptions> = {
    charLimit:                   20,
    charBoundary:                'min',
    errorMessage:                'A minimum of 20 characters is required.',
    requiredWhenEnforcedMessage: 'Please enter a value.',
    requiredWhenEnforced:        true,
    nullableWhenNotEnforced:     true,
} ) => {

    let whenCharLimitRequired = Yup.string()[ options.charBoundary ]( options.charLimit, options.errorMessage ),
        whenNotRequired       = Yup.string().nullable();

    if( options.requiredWhenEnforced ){
        whenCharLimitRequired = whenCharLimitRequired.required( options.requiredWhenEnforcedMessage )
    }
    if( options.nullableWhenNotEnforced ){
        whenNotRequired = whenNotRequired.nullable()
    }

    return subject.when( [], {
        is:        () => ( options.charLimit !== 0 ),
        then:      whenCharLimitRequired,
        otherwise: whenNotRequired,
    } );

}

export const addStringIsValidPhoneNumberTest: customTestFunction = ( schemaName = 'isUSPhoneNumber' ) => {

    /* Allowed Phone Formats:
     -------------------------------------------------------------------
     - 18001234567
     - 1 800 123 4567
     - +1 800 123-4567
     - +86 800 123 4567
     - 1-800-123-4567
     - 1 (800) 123-4567
     - (800)123-4567
     - (800) 123-4567
     - (800)1234567
     - 800-123-4567
     - 800.123.4567
     -------------------------------------------------------------------*/

    Yup.addMethod( Yup.string, schemaName, function(){

        const phoneRegExp = /^[+]?(\d{1,2})?[\s.-]?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/

        return ( this as StringSchema ).matches( phoneRegExp, 'Phone number is not valid' )
    } )
}

export const addStringIsValidPasswordTest: customTestFunction = ( schemaName = 'isValidPassword' ) => {

    /* Password Rules:
     -------------------------------------------------------------------
     - Passwords should be a minimum of 8 characters and a maximum of 64 characters
     - Password should be a combination of at least one character from three out of the following four listed character types :
     - English uppercase letters (A-Z),
     - English lowercase letters (a-z)
     - Base 10 digits (0-9)
     - Non-alphanumeric (such as ` ~ ! @ # $ % ^ & * ( ) _ + - = { } | \ : " ; ' < > ? , . / and space)
     -------------------------------------------------------------------*/

    Yup.addMethod( Yup.string, schemaName, function(){

        return ( this as StringSchema )
            .min( 8, 'Password needs to be at least 8 characters.' )
            .max( 64, 'Password cannot be longer than 64 characters.' )
            .test( {
                name:    'passwordValidity',
                message: 'Password must contain at least 3 of the required characters',
                test:    ( value: string ) => {

                    // iterate through each test
                    let tests = _.mapObject( PASSWORD_REQUIREMENTS_REGEX, ( regex ) => {
                        let requirementRegex = RegExp( regex );
                        return requirementRegex.test( value );
                    } );

                    // check testing outcomes
                    let nonOneOfEachTests   = _.omit( tests, 'HAS_ONE_OF_EACH' ),
                        hasOneOfEachChar    = tests.HAS_ONE_OF_EACH === true,
                        hasThreeOfFourChars = ( _.chain( nonOneOfEachTests ).values().compact().value().length >= 3 );

                    return !!( hasOneOfEachChar || hasThreeOfFourChars );

                }
            } );
    } )
}

export const transformCurrencyValue: customTransformFunction<T_OR_NULL<number>> = ( currentValue ) => {

    let amount = numeral( currentValue );

    return _.isNaN( amount.value() ) ? null : amount.value();
}

export const transformEmptyStringToNull: customTransformFunction<T_OR_NULL<string>> = ( currentValue ) => {

    return ( currentValue === '' ? null : currentValue )
}

export const transformNullToEmptyString: customTransformFunction<T_OR_NULL<string>> = ( currentValue ) => {

    return ( currentValue === null ? '' : currentValue )
}

export const useYupExtensions = () => {

    return {
        addStringIsDateFormatTest,
        addStringIsDateRangeFormatTest,
        addStringIsNumericTest,
        addStringIsValidPasswordTest,
        addStringIsValidPhoneNumberTest,
        addStringMinCharDescriptorTest,
        addStringDifferentiationTest,
        transformCurrencyValue,
        transformEmptyStringToNull,
        transformNullToEmptyString,
    }
};
